import React, { useState, useCallback, useEffect } from 'react'
import { useLazySearchFormTemplatesQuery, useSearchFormTemplatesQuery } from '@/core/services/form'
import { SearchFormTemplateDto } from '@/core/models/form.model'
import FormSelectionDialog from '../../components/forms/FormSelectionDialog'
import { useDebounce } from 'usehooks-ts'
import { PaginationState } from '@tanstack/react-table'

type FormSelectionDialogContainerProps = {
    open: boolean
    onClose: () => void
    onSelect: (templateId: string) => void
}

const DEFAULT_SEARCH_OPTIONS = {
    isActive: true,
}

const DEFAULT_PAGINATION: PaginationState = {
    pageIndex: 0,
    pageSize: 25,
}

const FormSelectionDialogContainer: React.FC<FormSelectionDialogContainerProps> = ({
    open,
    onClose,
    onSelect,
}) => {
    const [searchOptions, setSearchOptions] = useState<SearchFormTemplateDto>(DEFAULT_SEARCH_OPTIONS)
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)

    // Only debounce the search text, not the entire options object
    const debouncedSearchText = useDebounce(searchOptions.searchText, 300)

    // Initial data fetch with default options
    const { data: initialData } = useSearchFormTemplatesQuery({
        search: DEFAULT_SEARCH_OPTIONS,
        skip: 0,
        limit: DEFAULT_PAGINATION.pageSize,
        orderBy: 'createdAt',
        order: 'DESC',
    }, {
        skip: !open // Only fetch when dialog is open
    })

    // Lazy query for subsequent searches
    const [trigger, { data: searchData, isLoading, isFetching, error }] = useLazySearchFormTemplatesQuery()

    // Use initial data if no search has been performed
    const templates = searchData || initialData

    useEffect(() => {
        // Don't trigger if it's just the initial load with default options
        const isDefaultSearch =
            !searchOptions.searchText &&
            !searchOptions.categories?.length &&
            !searchOptions.tags?.length &&
            searchOptions.isActive === DEFAULT_SEARCH_OPTIONS.isActive &&
            pageIndex === 0 &&
            pageSize === DEFAULT_PAGINATION.pageSize

        if (open && !isDefaultSearch) {
            trigger({
                search: {
                    ...searchOptions,
                    searchText: debouncedSearchText
                },
                skip: pageIndex * pageSize,
                limit: pageSize,
                orderBy: 'createdAt',
                order: 'DESC',
            })
        }
    }, [open, pageIndex, pageSize, debouncedSearchText, searchOptions.categories, searchOptions.tags, searchOptions.isActive, trigger])

    const handleSearchChange = useCallback((newOptions: SearchFormTemplateDto) => {
        setSearchOptions(newOptions)
        // Reset to first page when search changes
        setPagination(prev => ({ ...prev, pageIndex: 0 }))
    }, [])

    const handlePageChange = useCallback((page: number, itemsPerPage: number) => {
        setPagination({
            pageIndex: page - 1, // Convert 1-based to 0-based
            pageSize: itemsPerPage
        })
    }, [])

    const handleItemsPerPageChange = useCallback((newPageSize: number) => {
        setPagination({
            pageIndex: 0,
            pageSize: newPageSize
        })
    }, [])

    return (
        <FormSelectionDialog
            open={open}
            onClose={onClose}
            onSelect={onSelect}
            templates={templates?.items}
            isLoading={isLoading || isFetching}
            searchOptions={searchOptions}
            onSearchChange={handleSearchChange}
            totalCount={templates?.totalCount || 0}
            page={pageIndex + 1} // Convert 0-based to 1-based
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handleItemsPerPageChange}
            error={error as Error}
        />
    )
}

export default FormSelectionDialogContainer 